import { Component, OnInit, OnDestroy } from '@angular/core';
import { UserService } from '../shared/services/user-services/user.service';
import { NavigationEnd, Router } from '@angular/router';
import { SidebarService } from '../shared/services/sidebar.service';
import { SubscriptionAccessService } from '../shared/services/subscription-access.service';
import { RbacService } from '../shared/services/rbac.service';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

const NAV = (name: string) => `/assets/image/nav/${name}.svg`;

@Component({
  selector: 'app-side-menu-bar',
  templateUrl: './side-menu-bar.component.html',
  styleUrls: ['./side-menu-bar.component.css'],
})
export class SideMenuBarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isExpanded = false;
  filteredMenu: any = []; // 👈 This is what your template should use

  loginUserType: string = '';
  openSubMenus: string[] = [];
  userId: any;

  // Subscription access
  subscriptionAccess: any = null;
  showTrialBanner: boolean = false;
  trialDaysRemaining: number = 0;

  // Search functionality
  searchTerm: string = '';
  showSearchResults: boolean = false;
  filteredSearchResults: any[] = [];

  // Hover functionality
  hoveredMenu: string = '';
  hoverTimeout: any;

  // Quick access items
  quickAccessItems: any[] = [];

  // Recent items
  recentItems: any[] = [];

  // All menu items for search
  allMenuItems: any[] = [];

  roleName: any

  projectStageCounts: Record<string, number> = {};

  /** Coalesces multiple `filterMenuItems()` runs into one stages/count HTTP cycle. */
  private readonly projectStageCountsTrigger$ = new Subject<void>();

  private readonly rolesAll = ['Admin', 'Organization', 'Staff', 'User', 'Branch', 'Employee'];
  private readonly rolesOrg = ['Admin', 'Organization', 'Branch'];
  private readonly rolesOrgEmp = ['Admin', 'Organization', 'Branch', 'Employee'];
  private readonly rolesAdmin = ['Admin'];

  sideM = [
    {
      image: NAV('grid'),
      name: 'sideMenu.adminAndOnboarding',
      route: '/dashboard',
      haveSubMenu: true,
      title: 'Admin & Onboarding',
      roles: this.rolesAll,
      subMenu: [
        { name: 'sideMenu.dashboard', route: '/dashboard', image: NAV('dashboard'), roles: this.rolesAll },
        { name: 'sideMenu.profile', route: '/profile', image: NAV('user'), roles: this.rolesAll },
        // { name: 'sideMenu.onboarding', route: '/company', image: NAV('rocket'), roles: this.rolesOrg },
        { name: 'sideMenu.companyAndBranches', route: '/company', image: NAV('building'), roles: this.rolesOrg },
        { name: 'sideMenu.subscription', route: '/subscription', image: NAV('card'), roles: this.rolesOrg },
        { name: 'sideMenu.clients', route: '/clients', image: NAV('users'), roles: this.rolesOrg },
        { name: 'sideMenu.employee', route: '/employees', image: NAV('user'), roles: this.rolesOrg },
        { name: 'sideMenu.vendorAccounts', route: '/vendor/list', image: NAV('store'), roles: this.rolesOrg },
        { name: 'sideMenu.machineOwners', route: '/machine-owners', image: NAV('truck'), roles: this.rolesOrg },
        { name: 'sideMenu.roleAndPermission', route: '/role-list', image: NAV('shield'), roles: this.rolesAdmin },
        { name: 'sideMenu.integrations', route: '/integrations', image: NAV('puzzle'), roles: this.rolesOrg },
      ],
    },
    {
      image: NAV('folder'),
      name: 'sideMenu.projectInitiation',
      route: '/projects',
      haveSubMenu: true,
      title: 'Project Initiation',
      roles: this.rolesOrgEmp,
      subMenu: [
        { name: 'sideMenu.projects', route: '/projects', image: NAV('clipboard'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.prePlanning', route: '/projects', image: NAV('list'), roles: this.rolesOrgEmp, queryParams: { stage: 'Pre-Planning' } },
        { name: 'sideMenu.estimation', route: '/estimation-stage-list', image: NAV('calculator'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.bom', route: '/bom', image: NAV('layers'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.aiBom', route: '/ai-bom', image: NAV('activity'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.boq', route: '/boq', image: NAV('table'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.aiBoq', route: '/ai-boq', image: NAV('cpu'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.wbsByProject', route: '/work-breakdown-structure/list', image: NAV('sitemap'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.wbsDeviationReport', route: '/work-breakdown-structure/deviation-report', image: NAV('diff'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.documentUpload', route: '/document-list', image: NAV('file'), roles: this.rolesOrgEmp },
        // { name: 'sideMenu.geospatial', route: '/geospatial', image: NAV('road'), roles: this.rolesOrgEmp },
      ],
    },
    {
      image: NAV('road'),
      name: 'sideMenu.geospatial',
      route: '/geospatial-dashboard',
      haveSubMenu: false,
      title: 'Geospatial',
      roles: this.rolesOrgEmp,
      subMenu: [],
    },
    {
      image: NAV('calendar'),
      name: 'sideMenu.planningAndProcurement',
      route: '/projects',
      haveSubMenu: true,
      title: 'Planning & Procurement',
      roles: this.rolesOrgEmp,
      subMenu: [
        { name: 'sideMenu.planning', route: '/projects', image: NAV('clipboard'), roles: this.rolesOrgEmp, queryParams: { stage: 'Planning' } },
        { name: 'sideMenu.designRevisions', route: '/projects', image: NAV('fileEdit'), roles: this.rolesOrgEmp, queryParams: { stage: 'Design Revisions & Scope Adjustments' } },
        { name: 'sideMenu.bidding', route: '/biding-list', image: NAV('gavel'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.vendorRanking', route: '/vendor-ranking', image: NAV('trophy'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.award', route: '/award-list', image: NAV('award'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.purchaseOrder', route: '/purchase-order-list', image: NAV('file'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.poAmendmentForm', route: '/po-amendment-list', image: NAV('fileEdit'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.mobilizationPlans', route: '/labour-mobilization/mobilization-plans', image: NAV('users'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.resourceMap', route: '/plm/resources', image: NAV('pin'), roles: this.rolesOrgEmp },
      ],
    },
    {
      image: NAV('ruler'),
      name: 'sideMenu.surveyAndSiteReadiness',
      route: '/projects',
      haveSubMenu: true,
      title: 'Survey & Site Readiness',
      roles: this.rolesOrgEmp,
      subMenu: [
        { name: 'sideMenu.survey', route: '/projects', image: NAV('ruler'), roles: this.rolesOrgEmp, queryParams: { stage: 'Survey' } },
        { name: 'sideMenu.inspection', route: '/inspection-stage-list', image: NAV('search'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.segmentInspection', route: '/segment-inspections', image: NAV('route'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.deviationsReport', route: '/deviations-report-list', image: NAV('diff'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.deviationReport', route: '/labour-mobilization/deviation-report', image: NAV('diff'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.designRevisions', route: '/projects', image: NAV('fileEdit'), roles: this.rolesOrgEmp, queryParams: { stage: 'Design Revisions & Scope Adjustments' } },
        { name: 'sideMenu.poAmendmentForm', route: '/po-amendment-list', image: NAV('fileEdit'), roles: this.rolesOrgEmp },
      ],
    },
    {
      image: NAV('hammer'),
      name: 'sideMenu.execution',
      route: '/projects',
      haveSubMenu: true,
      title: 'Execution',
      roles: this.rolesOrgEmp,
      subMenu: [
        { name: 'sideMenu.siv', route: '/siv-list', image: NAV('inbox'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.srv', route: '/srv-list', image: NAV('outbox'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.execution', route: '/projects', image: NAV('clipboard'), roles: this.rolesOrgEmp, queryParams: { stage: 'Execution' } },
        { name: 'sideMenu.pavementProgress', route: '/plm/pavement', image: NAV('layers'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.workOrder', route: '/work-orders', image: NAV('file'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.dailyReports', route: '/labour-mobilization/daily-reports', image: NAV('list'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.progressReport', route: '/progress-report', image: NAV('trending'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.monitoring', route: '/monitoring-list', image: NAV('activity'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.material', route: '/materials', image: NAV('box'), roles: this.rolesOrg },
        { name: 'sideMenu.machine', route: '/machines', image: NAV('cog'), roles: this.rolesOrg },
        { name: 'sideMenu.crewMachine', route: '/user-machine', image: NAV('users'), roles: this.rolesOrg },
        { name: 'sideMenu.machineReservations', route: '/machine-reservations', image: NAV('calendar'), roles: this.rolesOrg },
        { name: 'sideMenu.attendance', route: '/attendance', image: NAV('clock'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.invoice', route: '/invoice-list', image: NAV('receipt'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.projectBills', route: '/project-bills', image: NAV('banknote'), roles: this.rolesOrg },
      ],
    },
    {
      image: NAV('flag'),
      name: 'sideMenu.closure',
      route: '/projects',
      haveSubMenu: true,
      title: 'Closure',
      roles: this.rolesOrgEmp,
      subMenu: [
        { name: 'sideMenu.closure', route: '/projects', image: NAV('flag'), roles: this.rolesOrgEmp, queryParams: { stage: 'Closure' } },
        { name: 'sideMenu.acceptanceWcc', route: '/plm/closure', image: NAV('badge'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.plmOps', route: '/plm/ops', image: NAV('cpu'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.invoiceList', route: '/invoice-list', image: NAV('receipt'), roles: this.rolesOrg },
        { name: 'sideMenu.paymentList', route: '/payment-list', image: NAV('banknote'), roles: this.rolesOrg },
      ],
    },
    {
      image: NAV('headset'),
      name: 'sideMenu.support',
      route: '/support-ticket/add',
      haveSubMenu: true,
      title: 'Support',
      roles: this.rolesOrgEmp,
      subMenu: [
        { name: 'sideMenu.supportTicket', route: '/support-ticket/add', image: NAV('ticket'), roles: this.rolesOrgEmp },
        { name: 'sideMenu.contactUs', route: '/contact-us', image: NAV('mail'), roles: this.rolesOrgEmp },
      ],
    },
    {
      image: NAV('dots'),
      name: 'sideMenu.general',
      haveSubMenu: true,
      title: 'General',
      roles: this.rolesAll,
      subMenu: [
        { name: 'sideMenu.logout', image: NAV('logout'), roles: this.rolesAll, action: 'logout', title: 'Log out' },
      ],
    },
  ];

  constructor(
    private userService: UserService,
    private router: Router,
    public sidebarService: SidebarService,
    private subscriptionAccessService: SubscriptionAccessService,
    private rbacService: RbacService,
    private translate: TranslateService,
    private apiService: ApiService
  ) {
    const user = localStorage.getItem('user') as any;
    const userParse = JSON.parse(user);
    this.loginUserType = userParse.user?.role_name;
    this.userId = this.userService.getUserDetailsFromSession()?._id;
  }

  toggleMenu(): void {
    this.isExpanded = !this.isExpanded;
    this.sidebarService.setExpanded(this.isExpanded);
  }

  ngOnInit(): void {
    // Subscribe to sidebar state changes
    this.sidebarService.isExpanded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(expanded => {
        this.isExpanded = expanded;
      });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncOpenSectionFromRoute());

    // Initialize menu filtering first
    this.filterMenuItems();

    // Subscribe to subscription access changes
    this.subscriptionAccessService.getSubscriptionAccess()
      .pipe(takeUntil(this.destroy$))
      .subscribe(access => {
        this.subscriptionAccess = access;
        this.showTrialBanner = access?.isTrial || false;
        this.trialDaysRemaining = access?.trialDaysRemaining || 0;
        this.filterMenuItems();
      });

    // Also subscribe to RBAC changes
    this.rbacService.getUserPermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterMenuItems();
      });

    // Initialize search data
    this.initializeSearchData();
    
    // Initialize quick access items
    this.initializeQuickAccess();
    
    // Load recent items
    this.loadRecentItems();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initializeSearchData();
      this.initializeQuickAccess();
    });

    this.projectStageCountsTrigger$
      .pipe(
        debounceTime(120),
        switchMap(() => this.loadProjectStageCounts$()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Match menu `roles` against the logged-in user's `role_name`.
   * DB/login often uses "Organization Admin" while menu entries use "Organization".
   */
  private roleMatchesMenu(allowedRoles: string[]): boolean {
    const r = this.loginUserType;
    if (!r || !allowedRoles?.length) return false;
    if (allowedRoles.includes(r)) return true;
    if (r === 'Organization Admin' && allowedRoles.includes('Organization')) return true;
    if (r === 'Organization' && allowedRoles.includes('Organization Admin')) return true;
    return false;
  }

  /**
   * Filter menu items based on role, RBAC permissions, and subscription access
   * Priority: Admin > Role > Subscription > RBAC (if configured)
   */
  filterMenuItems(): void {
    if (this.loginUserType === 'Vendor') {
      this.filteredMenu = [
        {
          image: NAV('store'),
          name: 'sideMenu.vendorMyResources',
          route: '/vendor/resources',
          haveSubMenu: false,
          title: 'Vendor Resources',
          roles: ['Vendor'],
          subMenu: [],
        },
        ...this.sideM.filter((i) => i.title === 'Log out' || i.title === 'General'),
      ];
      this.initializeSearchData();
      this.initializeQuickAccess();
      return;
    }

    const access = this.subscriptionAccessService.getCurrentAccess();
    const isAdmin = this.rbacService.isAdmin();
    const hasRBACConfigured = this.rbacService.getCurrentPermissions() !== null;
    
    console.log('Menu Filtering:', {
      loginUserType: this.loginUserType,
      isAdmin,
      hasRBACConfigured,
      subscriptionAccess: access
    });
    
    this.filteredMenu = this.sideM
      .map(item => {
        // Filter submenu items
        const subMenu = Array.isArray(item.subMenu)
          ? item.subMenu.filter(sub => {
            // 1. Check role access first
            if (!this.roleMatchesMenu(sub.roles)) {
              return false;
            }

            // 2. Admin always has access
            if (isAdmin) {
              return true;
            }

            // 3. Check subscription module access (higher priority than RBAC)
            if ('route' in sub && sub.route) {
              const route = sub.route.startsWith('/') ? sub.route : '/' + sub.route;
              if (!this.subscriptionAccessService.canAccessRoute(route)) {
                console.log(`Subscription blocked: ${route}`);
                return false;
              }
            }

            // 4. Check RBAC permissions only if RBAC is configured
            // If no permissions are set, allow access based on subscription
            if (hasRBACConfigured && 'route' in sub && sub.route) {
              const route = sub.route.startsWith('/') ? sub.route : '/' + sub.route;
              const module = this.rbacService.routeToModule(route);
              
              // Only block if RBAC explicitly denies access
              if (!this.rbacService.canAccess(module)) {
                console.log(`RBAC blocked: ${module} for route ${route}`);
                return false;
              }
            }

            return true;
          })
          : [];

        return { ...item, subMenu };
      })
      .filter(item => {
        // Always show logout / general
        if (item.title === 'Log out' || item.title === 'General') {
          return true;
        }

        // 1. Check role access first
        if (!this.roleMatchesMenu(item.roles)) {
          console.log(`Role blocked: ${item.title} - roles: ${item.roles}, userType: ${this.loginUserType}`);
          return false;
        }

        // 2. Admin always has access
        if (isAdmin) {
          return true;
        }

        // 3. Check subscription module access first (before RBAC)
        if (item.route) {
          const route = item.route.startsWith('/') ? item.route : '/' + item.route;
          const canAccessSub = this.subscriptionAccessService.canAccessRoute(route);
          
          if (!canAccessSub) {
            // If main item is blocked by subscription but has accessible submenu, still show
            if (item.subMenu && item.subMenu.length > 0) {
              return true;
            }
            console.log(`Subscription blocked main item: ${item.title} for route ${route}`);
            return false;
          }
        }

        // 4. Check RBAC permissions only if RBAC is configured
        if (hasRBACConfigured && item.route) {
          const route = item.route.startsWith('/') ? item.route : '/' + item.route;
          const module = this.rbacService.routeToModule(route);
          
          // Only block if RBAC explicitly denies access
          if (!this.rbacService.canAccess(module)) {
            // If main item is not accessible but has accessible submenu, still show
            if (item.subMenu && item.subMenu.length > 0) {
              return true;
            }
            console.log(`RBAC blocked main item: ${item.title} for module ${module}`);
            return false;
          }
        }

        // If has submenu, check if any submenu item is accessible
        if (item.subMenu && item.subMenu.length > 0) {
          return item.subMenu.length > 0;
        }

        // If no route, allow access if role matches
        return true;
      });
      
    console.log('Filtered menu items:', this.filteredMenu.map((m: any) => m.title));
    this.initializeSearchData();
    this.initializeQuickAccess();

    this.scheduleProjectStageCounts();
    this.syncOpenSectionFromRoute();
  }

  /** Schedules a debounced load so startup + subscription + RBAC don’t triple-hit the API. */
  private scheduleProjectStageCounts(): void {
    this.projectStageCountsTrigger$.next();
  }

  private menusWithProjectLinks(): any[] {
    return (this.filteredMenu || []).filter((m: any) =>
      Array.isArray(m?.subMenu) && m.subMenu.some((s: any) => s?.route === '/projects')
    );
  }

  private loadProjectStageCounts$(): Observable<void> {
    const projectsItem = this.menusWithProjectLinks()[0];
    if (!projectsItem?.subMenu?.length) {
      return of(undefined);
    }

    return this.apiService.getProjectStagesSummary().pipe(
      catchError(() => of(null)),
      switchMap((res: any): Observable<void> => {
        const rows =
          res?.data?.stages ??
          (Array.isArray(res?.data) ? res.data : null);
        const nextCounts: Record<string, number> = {};
        if (Array.isArray(rows) && rows.length > 0) {
          for (const r of rows) {
            const stage = String(r?.stage ?? '').trim();
            const raw = r?.project_count ?? r?.projectCount ?? 0;
            const c = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
            if (stage) nextCounts[stage] = Number.isFinite(c) ? c : 0;
          }
          this.applyStageCountsToAllProjectMenus(nextCounts);
          return of(undefined);
        }

        // Summary succeeded but no grouped rows (e.g. no stage values): one list call for total only.
        if (res !== null) {
          return this.apiService.getProjectCommonAll({ page: 1, limit: 1 }).pipe(
            catchError(() => of({ pagination: { totalCount: 0 } } as any)),
            tap((r: any): void => {
              const totalAll = Number(r?.pagination?.totalCount ?? 0) || 0;
              this.applyStageCountsToAllProjectMenus({}, totalAll);
            }),
            map((): undefined => undefined)
          );
        }

        // `/project-common/stages` failed — legacy multi-call fallback (same visibility as list).
        return this.loadProjectStageCountsViaProjectList$(projectsItem);
      })
    );
  }

  private applyStageCountsToAllProjectMenus(
    nextCounts: Record<string, number>,
    allProjectsTotalOverride?: number
  ): void {
    this.projectStageCounts = nextCounts;
    for (const menu of this.menusWithProjectLinks()) {
      this.applyStageCountsToProjectsSubmenu(menu, nextCounts, allProjectsTotalOverride);
    }
  }

  private applyStageCountsToProjectsSubmenu(
    projectsItem: any,
    nextCounts: Record<string, number>,
    /** When stage breakdown is unknown, use server total for “All projects” instead of summing badges. */
    allProjectsTotalOverride?: number
  ): void {
    this.projectStageCounts = nextCounts;
    projectsItem.subMenu = projectsItem.subMenu.map((sub: any) => {
      const stage = sub?.queryParams?.stage;
      if (sub?.route === '/projects' && typeof stage === 'string' && stage.trim().length) {
        return { ...sub, count: this.projectStageCounts[stage] ?? 0 };
      }
      if (sub?.route === '/projects' && !sub?.queryParams) {
        const total =
          allProjectsTotalOverride !== undefined
            ? allProjectsTotalOverride
            : Object.values(this.projectStageCounts).reduce(
                (a, b) => a + (Number(b) || 0),
                0
              );
        return { ...sub, count: total };
      }
      return sub;
    });
  }

  /** Per-stage totals via `GET /project-common` pagination (same visibility rules as the list). */
  private loadProjectStageCountsViaProjectList$(projectsItem: any): Observable<void> {
    const stageNames = [
      'Pre-Planning',
      'Design Revisions & Scope Adjustments',
      'Planning',
      'Survey',
      'Execution',
      'Closure',
    ];

    const total$ = this.apiService.getProjectCommonAll({ page: 1, limit: 1 }).pipe(
      catchError(() => of({ pagination: { totalCount: 0 } } as any))
    );
    const perStage$ = stageNames.map((stage) =>
      this.apiService.getProjectCommonAll({ page: 1, limit: 1, stage }).pipe(
        catchError(() => of({ pagination: { totalCount: 0 } } as any)),
        map((r: any) => ({
          stage,
          count: Number(r?.pagination?.totalCount ?? 0) || 0,
        }))
      )
    );

    return forkJoin([total$, ...perStage$]).pipe(
      tap((results: any[]): void => {
        const totalRes = results[0];
        const totalAll = Number(totalRes?.pagination?.totalCount ?? 0) || 0;
        const nextCounts: Record<string, number> = {};
        for (let i = 1; i < results.length; i++) {
          const row = results[i];
          if (row?.stage) nextCounts[row.stage] = row.count ?? 0;
        }
        this.applyStageCountsToAllProjectMenus(nextCounts);
        const allLink = projectsItem.subMenu.find(
          (s: any) => s?.route === '/projects' && !s?.queryParams
        );
        if (allLink) {
          const idx = projectsItem.subMenu.indexOf(allLink);
          if (idx >= 0) {
            projectsItem.subMenu[idx] = { ...allLink, count: totalAll };
          }
        }
      }),
      map((): undefined => undefined)
    );
  }

  // Search functionality
  initializeSearchData(): void {
    this.allMenuItems = [];
    
    this.filteredMenu.forEach((item: any) => {
      // Add main menu item
      if (item.route) {
        this.allMenuItems.push({
          name: item.name,
          route: item.route,
          image: item.image,
          category: 'sideMenu.searchCategoryMain',
          type: 'main'
        });
      }
      
      // Add submenu items
      if (item.subMenu) {
        item.subMenu.forEach((sub: any) => {
          if (!sub.route) {
            return;
          }
          this.allMenuItems.push({
            name: sub.name,
            route: sub.route,
            image: sub.image,
            category: item.name,
            type: 'submenu'
          });
        });
      }
    });
  }

  onSearchInput(): void {
    if (!this.searchTerm.trim()) {
      this.filteredSearchResults = [];
      return;
    }

    const searchLower = this.searchTerm.toLowerCase();
    this.filteredSearchResults = this.allMenuItems
      .filter(item => {
        const nameMatch = this.translate.instant(item.name).toLowerCase();
        const catMatch = this.translate.instant(item.category).toLowerCase();
        return nameMatch.includes(searchLower) || catMatch.includes(searchLower);
      })
      .slice(0, 8);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filteredSearchResults = [];
    this.showSearchResults = false;
  }

  onSearchBlur(): void {
    setTimeout(() => {
      this.showSearchResults = false;
    }, 200);
  }

  navigateToSearchResult(result: any): void {
    this.addToRecentItems(result);
    this.router.navigate(['/home/' + result.route]);
    this.clearSearch();
  }

  // Quick access functionality
  initializeQuickAccess(): void {
    const candidates = [
      { name: 'sideMenu.dashboard', route: '/dashboard', image: NAV('dashboard') },
      { name: 'sideMenu.geospatial', route: '/geospatial-dashboard', image: NAV('road') },
      { name: 'sideMenu.projects', route: '/projects', image: NAV('folder') },
      { name: 'sideMenu.supportTicket', route: '/support-ticket/add', image: NAV('ticket') },
      { name: 'sideMenu.financial', route: '/payment-list', image: NAV('banknote') },
    ];
    this.quickAccessItems = candidates.filter((item) => {
      const menuItem = this.filteredMenu.find(
        (m: any) => m.name === item.name || this.menuContainsRoute(m, item.route)
      );
      return menuItem && this.roleMatchesMenu(menuItem.roles);
    });
  }

  /** True if main menu route matches or any submenu route matches `route` (with optional leading slash). */
  private menuContainsRoute(menu: any, route: string): boolean {
    const normalized = route.startsWith('/') ? route : '/' + route;
    const main = menu.route
      ? (menu.route.startsWith('/') ? menu.route : '/' + menu.route)
      : '';
    if (main === normalized) {
      return true;
    }
    return (
      Array.isArray(menu.subMenu) &&
      menu.subMenu.some((s: any) => {
        const sr = s.route ? (s.route.startsWith('/') ? s.route : '/' + s.route) : '';
        return sr === normalized;
      })
    );
  }

  navigateToQuickAccess(item: any): void {
    this.addToRecentItems(item);
    this.router.navigate(['/home/' + item.route]);
  }

  // Recent items functionality
  loadRecentItems(): void {
    const recent = localStorage.getItem('recentItems');
    if (recent) {
      this.recentItems = JSON.parse(recent).slice(0, 5); // Keep only 5 recent items
    }
  }

  addToRecentItems(item: any): void {
    const recent = [...this.recentItems];
    
    // Remove if already exists
    const existingIndex = recent.findIndex(r => r.route === item.route);
    if (existingIndex > -1) {
      recent.splice(existingIndex, 1);
    }
    
    // Add to beginning
    recent.unshift(item);
    
    // Keep only 5 items
    this.recentItems = recent.slice(0, 5);
    
    // Save to localStorage
    localStorage.setItem('recentItems', JSON.stringify(this.recentItems));
  }

  navigateToRecent(item: any): void {
    this.router.navigate(['/home/' + item.route]);
  }

  // Hover functionality
  onMenuHover(title: string): void {
    if (!this.isExpanded) {
      this.hoveredMenu = title;
    }
  }

  onMenuLeave(): void {
    if (!this.isExpanded) {
      this.hoverTimeout = setTimeout(() => {
        this.hoveredMenu = '';
      }, 300);
    }
  }

  onSubmenuHover(title: string): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    this.hoveredMenu = title;
  }

  onSubmenuLeave(): void {
    this.hoveredMenu = '';
  }

  toggleSubMenu(title: string) {
    if (this.openSubMenus.includes(title)) {
      this.openSubMenus = [];
    } else {
      this.openSubMenus = [title];
    }
  }

  isSubMenuOpen(title: string): boolean {
    if (this.searchTerm.trim()) {
      return this.groupMatchesSearch(this.filteredMenu.find((m: any) => m.title === title));
    }
    return this.openSubMenus.includes(title);
  }

  isSubActive(sub: { route?: string; queryParams?: Record<string, string> }): boolean {
    if (!sub?.route) return false;
    const path = '/home' + (sub.route.startsWith('/') ? sub.route : '/' + sub.route);
    const [rawPath, rawQuery = ''] = this.router.url.split('?');
    const urlPath = rawPath.replace(/\/$/, '') || '/';
    const stage = new URLSearchParams(rawQuery).get('stage');
    const pathMatch = urlPath === path || urlPath.startsWith(path + '/');
    if (!pathMatch) return false;
    if (sub.queryParams?.['stage']) {
      return stage === sub.queryParams['stage'];
    }
    if (sub.route === '/projects') {
      return !stage;
    }
    return true;
  }

  isSectionActive(item: { subMenu?: any[] }): boolean {
    return (item.subMenu || []).some((sub) => this.isSubActive(sub));
  }

  visibleChildren(item: { subMenu?: any[]; title?: string; name?: string }): any[] {
    const kids = item.subMenu || [];
    const q = this.searchTerm.trim().toLowerCase();
    if (!q) return kids;
    const parentHit =
      (item.title || '').toLowerCase().includes(q) ||
      this.translate.instant(item.name || '').toLowerCase().includes(q);
    if (parentHit) return kids;
    return kids.filter((sub) => this.translate.instant(sub.name || '').toLowerCase().includes(q));
  }

  menuVisible(item: { subMenu?: any[]; title?: string; name?: string; haveSubMenu?: boolean }): boolean {
    const q = this.searchTerm.trim().toLowerCase();
    if (!q) return true;
    if ((item.title || '').toLowerCase().includes(q)) return true;
    if (this.translate.instant(item.name || '').toLowerCase().includes(q)) return true;
    return this.visibleChildren(item).length > 0;
  }

  private groupMatchesSearch(item: any): boolean {
    if (!item) return false;
    return this.menuVisible(item);
  }

  private syncOpenSectionFromRoute(): void {
    if (this.searchTerm.trim()) return;
    const match = (this.filteredMenu || []).find((item: any) => this.isSectionActive(item));
    if (match?.title) {
      this.openSubMenus = [match.title];
    }
  }

  onClick(hasSubMenu: boolean, title: string, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (title == 'Log out') {
      this.userService.logout();
      this.router.navigate(['/']);
      return;
    }

    if (hasSubMenu) {
      if (!this.isExpanded) {
        this.sidebarService.setExpanded(true);
        setTimeout(() => this.toggleSubMenu(title), 180);
      } else {
        this.toggleSubMenu(title);
      }
    }
  }

  onSubmenuClick(event: Event, subItem: any): void {
    event.stopPropagation();
    this.addToRecentItems(subItem);
  }

  onDirectLinkClick(event: Event, item: any): void {
    event.stopPropagation();
    this.addToRecentItems(item);
  }

}